/**
 * <LaceComputeModal> — compute/refresh the LACE 30-day readmission index.
 *
 * The clinician enters length of stay / acuity / Charlson comorbidity index /
 * ED visits prior 6mo for a patient on their roster. We POST to
 * /api/risk/readmission/{patient_id} which scores LACE, combines with the
 * patient's recent NEWS2 trend (server-side), and writes the high-risk flag
 * + care-team alert when applicable.
 *
 * Honest framing: the modal labels LACE as a validated index (van Walraven
 * 2010), max score 19; the server returns a disclaimer ("not a guarantee")
 * that we render below the result.
 */
import React, { useMemo, useState } from 'react';
import axios from '../../lib/api';
import { X, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import useModalClose, { CLOSE_BTN_STYLE } from '../../hooks/useModalClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const laceClient = (i) => {
  const los = Number(i.length_of_stay_days || 0);
  let L = 0;
  if (los <= 0) L = 0;
  else if (los === 1) L = 1;
  else if (los === 2) L = 2;
  else if (los === 3) L = 3;
  else if (los <= 6) L = 4;
  else if (los <= 13) L = 5;
  else L = 7;
  const A = i.acute_emergent_admission ? 3 : 0;
  const C = Math.min(5, Math.max(0, Number(i.charlson_comorbidity_index || 0)));
  const E = Math.min(4, Math.max(0, Number(i.ed_visits_prior_6mo || 0)));
  const score = L + A + C + E;
  const band = score >= 10 ? 'high' : score >= 5 ? 'moderate' : 'low';
  return { score, band, L, A, C, E };
};

const BAND_COLOR = {
  low: 'border-cn-success/40 bg-cn-success/8 text-cn-success',
  moderate: 'border-cn-accent/40 bg-cn-accent/10 text-cn-accent',
  high: 'border-red-500/40 bg-red-500/10 text-red-600',
};

export const LaceComputeModal = ({ open, patient, onClose, onSaved, getAuthHeader }) => {
  const { backdropProps, cardProps } = useModalClose({ open, onClose });
  const [form, setForm] = useState({
    length_of_stay_days: '',
    acute_emergent_admission: true,
    charlson_comorbidity_index: '',
    ed_visits_prior_6mo: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const preview = useMemo(() => laceClient(form), [form]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    const payload = {
      length_of_stay_days: Number(form.length_of_stay_days || 0),
      acute_emergent_admission: !!form.acute_emergent_admission,
      charlson_comorbidity_index: Number(form.charlson_comorbidity_index || 0),
      ed_visits_prior_6mo: Number(form.ed_visits_prior_6mo || 0),
    };
    try {
      const r = await axios.post(`${API}/risk/readmission/${patient.patient_id}`, payload, getAuthHeader?.());
      const band = r.data?.combined_band || preview.band;
      toast.success(`LACE ${r.data?.lace?.score}/19 — ${band} combined risk`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not compute LACE.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div {...backdropProps} className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="lace-modal">
      <div {...cardProps} className="bg-white dark:bg-cn-ink-900 rounded-3xl border border-cn-border/60 shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-cn-border/60 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Update LACE · 30-day readmission</p>
            <h2 className="text-lg font-display font-extrabold text-cn-text dark:text-white">
              {patient?.patient_name || `Patient ${patient?.patient_id?.slice(0, 8)}`}
            </h2>
          </div>
          <button onClick={onClose} className="touch-manipulation rounded-full hover:bg-cn-bg"
                  style={CLOSE_BTN_STYLE} aria-label="Close LACE modal"
                  data-testid="lace-modal-close">
            <X className="w-5 h-5 text-cn-text-muted" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] font-bold uppercase tracking-wider text-cn-text-muted mb-1">Length of stay (days)</span>
              <input type="number" inputMode="numeric" min="0" max="365"
                value={form.length_of_stay_days}
                onChange={(e) => set('length_of_stay_days', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-cn-border bg-white dark:bg-cn-ink-800 text-cn-text dark:text-white text-sm"
                data-testid="lace-input-los" />
            </label>
            <label className="block">
              <span className="block text-[11px] font-bold uppercase tracking-wider text-cn-text-muted mb-1">Charlson comorbidity</span>
              <input type="number" inputMode="numeric" min="0" max="37"
                value={form.charlson_comorbidity_index}
                onChange={(e) => set('charlson_comorbidity_index', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-cn-border bg-white dark:bg-cn-ink-800 text-cn-text dark:text-white text-sm"
                data-testid="lace-input-charlson" />
              <span className="text-[10px] text-cn-text-muted">Capped at 5 in LACE</span>
            </label>
            <label className="block">
              <span className="block text-[11px] font-bold uppercase tracking-wider text-cn-text-muted mb-1">ED visits (prior 6mo)</span>
              <input type="number" inputMode="numeric" min="0" max="20"
                value={form.ed_visits_prior_6mo}
                onChange={(e) => set('ed_visits_prior_6mo', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-cn-border bg-white dark:bg-cn-ink-800 text-cn-text dark:text-white text-sm"
                data-testid="lace-input-ed-visits" />
              <span className="text-[10px] text-cn-text-muted">Capped at 4 in LACE</span>
            </label>
            <label className="block">
              <span className="block text-[11px] font-bold uppercase tracking-wider text-cn-text-muted mb-1">Admission type</span>
              <select
                value={form.acute_emergent_admission ? 'emergent' : 'elective'}
                onChange={(e) => set('acute_emergent_admission', e.target.value === 'emergent')}
                className="w-full px-3 py-2 rounded-lg border border-cn-border bg-white dark:bg-cn-ink-800 text-cn-text dark:text-white text-sm"
                data-testid="lace-input-acuity"
              >
                <option value="emergent">Acute / emergent</option>
                <option value="elective">Elective</option>
              </select>
            </label>
          </div>

          <div className={`rounded-xl border-2 p-4 ${BAND_COLOR[preview.band]}`} data-testid="lace-preview">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                <p className="text-xs font-bold uppercase tracking-wider">LACE preview</p>
              </div>
              <p className="text-2xl font-display font-extrabold" data-testid="lace-preview-score">{preview.score}/19</p>
            </div>
            <p className="text-[11px] mt-1 font-semibold uppercase" data-testid="lace-preview-band">
              {preview.band} readmission risk
            </p>
            <p className="text-[11px] mt-2 opacity-80 leading-snug">
              L={preview.L} · A={preview.A} · C={preview.C} · E={preview.E}
            </p>
          </div>

          <p className="text-[11px] text-cn-text-muted leading-snug">
            LACE index (van Walraven CMAJ 2010) — validated risk score for 30-day
            readmission / death. Clinical decision support, never a guarantee.
            CaraLoom combines this with the patient's recent NEWS2 trend to set the
            combined band shown on your dashboard.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-cn-border/60 flex justify-end gap-2">
          <button onClick={onClose} className="cn-btn cn-btn-outline !text-sm" disabled={submitting}>Cancel</button>
          <button onClick={submit} className="cn-btn cn-btn-primary !text-sm" disabled={submitting}
                  data-testid="lace-submit">
            {submitting ? 'Computing…' : 'Save LACE'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LaceComputeModal;
